// ════════════════════════════════════════════════════════════════
// care_plans.targets key  →  canonical measure vocabulary
// ════════════════════════════════════════════════════════════════
// ONE fold, because the two vocabularies genuinely differ and the mismatch has
// cost real patients their targets. The care plan (what a human authors) spells
// A1c `a1c` in 53 of 54 live prod plans; every consumer of a measure spells it
// `hba1c`. Before this map existed, that single gap meant:
//   • WF46 created an orphan `a1c` care_goal row nothing joins, and its sweep
//     deactivated the real `hba1c` row — permanently (live: care periods 131, 164).
//   • WF70's note printed the care GOAL's text under a line claiming the care PLAN.
//   • the patient's goal card rendered a raw "a1c" tile beside its own hba1c goal.
//
// DIRECTION IS FIXED: plan key → measure_type, never the reverse. `measure_type`
// is immovable because five independent things pin it —
//   1. measure_state carries UNIQUE (care_period_id, measure_type) and is seeded
//      FROM the goal set; both patient renderers merge on it, so renaming a goal
//      costs the patient their baseline and current value.
//   2. WF9's writers hardcode 'bp_systolic' | 'bp_diastolic' | 'weight' | 'bmi'.
//   3. WF37 names it canonical and bridges its own CMS slugs onto it.
//   4. condition_mapping.measure_types (care-plans-schema.sql) declares exactly
//      this vocabulary — zero occurrences of a1c / non_hdl / egfr_decline_yr.
//   5. eight label/unit maps across the app and the companion spark.
// And renaming the PLAN side would change WF3's BP-trend floor, which reads
// `targets.bp_systolic` verbatim and silently defaults to 130 — a clinician-
// alerting change — as well as 53 live JSONB blobs.
//
// DENY BY DEFAULT. An unmapped key yields { measureType: null, known: false }.
// Callers MUST drop it and record a marker, never invent a goal for it: silently
// creating rows for keys nothing feeds is how the orphan `a1c` row appeared.
//
// Pure ESM data + functions, no imports, no process.env — it ships inside the
// browser bundle (app/src/logic/carePlanTargets.ts re-exports it) and is inlined
// verbatim into WF1's seeder. Keep it that way.
// ════════════════════════════════════════════════════════════════

/* CARE-PLAN-TARGET-MAP-START */

// null = this plan target is deliberately NOT a goal.
const PLAN_TARGET_TO_MEASURE = {
  bp_systolic: 'bp_systolic',
  bp_diastolic: 'bp_diastolic',
  weight: 'weight',
  bmi: 'bmi',
  ldl_c: 'ldl_c',
  a1c: 'hba1c',   // the authored spelling: 53 of 54 live prod plans
  hba1c: 'hba1c', // legacy spelling: 1 live prod plan
  egfr: 'egfr',
  uacr: 'uacr',
  // ── deliberately NOT goals ──
  non_hdl: null,         // no measure_state feed, no label map, not an OAP measure
  egfr_decline_yr: null, // a RATE of decline; the egfr goal is a LEVEL. Folding them is a unit error.
  waist: null,           // CMS reporting only, never a patient goal
  steps_daily: null,     // back-office-addable, no measure feed — would read "Awaiting data" forever
};

// An operator whose `value` is directly comparable in the measure's own unit.
// `reduce_by_pct` is NOT: its value is a percentage, and storing 7 in an absolute
// column is how a weight goal came to draw a 7 kg (15 lb) target line on a chart
// of ~190 lb readings (live: care periods 26, 131, 164).
const COMPARISON_OPERATOR = /^[<>=≤≥]=?$/;

function measureTypeForPlanTarget(planKey) {
  const k = String(planKey == null ? '' : planKey).trim();
  if (!Object.prototype.hasOwnProperty.call(PLAN_TARGET_TO_MEASURE, k)) {
    return { measureType: null, known: false };
  }
  return { measureType: PLAN_TARGET_TO_MEASURE[k], known: true };
}

function isComparableOperator(op) {
  return op != null && COMPARISON_OPERATOR.test(String(op).trim());
}

// A plan target entry → the goal fields it implies. `value` survives ONLY when the
// operator makes it comparable; otherwise the number is a percentage and the text
// carries the whole meaning.
function goalFieldsFromPlanTarget(entry) {
  const e = entry && typeof entry === 'object' ? entry : {};
  const comparable = isComparableOperator(e.operator);
  const raw = e.value;
  // Number('') === 0 — an empty target must not become a numeric zero target.
  const n = raw === '' || raw === null || raw === undefined ? null : Number(raw);
  // Deliberately NOT target_type. Nothing reads it (grep: app, spark, workflows — zero
  // consumers outside the two writers), the plan has no opinion on it, and WF1's
  // condition gates know more than an operator does (BMI <=25 is an improvement goal for
  // an obese patient and a control goal for anyone else). Emitting it here would
  // reintroduce the two-writers-disagree pattern on a field with no reader.
  return {
    target_value_num: comparable && Number.isFinite(n) ? n : null,
    target_operator: e.operator == null ? null : String(e.operator),
    target_value_text: e.display == null ? null : String(e.display),
  };
}

// Re-key an authored targets object into the measure vocabulary, dropping the
// no-goal class. Returns { targets, dropped } — `dropped` is what the caller must
// record rather than silently swallow.
function foldPlanTargets(targets) {
  const out = {};
  const dropped = [];
  // `targets` arrives as an OBJECT keyed by plan key on care_plans.targets, but some payloads
  // deliver it as an array of {measure_type, ...} rows. Accept both — rejecting the array
  // silently returned "no target for any measure", which is indistinguishable from a plan
  // that authored none.
  let src = targets && typeof targets === 'object' ? targets : {};
  if (Array.isArray(src)) {
    const byKey = {};
    for (const row of src) {
      const k = row && row.measure_type;
      if (k != null && !Object.prototype.hasOwnProperty.call(byKey, k)) byKey[k] = row;
    }
    src = byKey;
  }
  for (const key of Object.keys(src)) {
    const { measureType, known } = measureTypeForPlanTarget(key);
    if (measureType == null) { dropped.push({ key, reason: known ? 'not_a_goal' : 'unmapped' }); continue; }
    // First writer wins so the canonical spelling cannot be clobbered by a legacy alias.
    if (!Object.prototype.hasOwnProperty.call(out, measureType)) out[measureType] = src[key];
  }
  return { targets: out, dropped };
}

// The authored target for a measure, honouring the alias. Use this instead of
// `targets[measureType]` — that lookup misses `a1c` and is what made HbA1c fall
// through to the care goal's own text.
function planTargetForMeasure(targets, measureType) {
  const folded = foldPlanTargets(targets).targets;
  return Object.prototype.hasOwnProperty.call(folded, measureType) ? folded[measureType] : null;
}

/* CARE-PLAN-TARGET-MAP-END */

export {
  PLAN_TARGET_TO_MEASURE,
  COMPARISON_OPERATOR,
  measureTypeForPlanTarget,
  isComparableOperator,
  goalFieldsFromPlanTarget,
  foldPlanTargets,
  planTargetForMeasure,
};
